<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <ui-chips v-model="orderStatus" :options="statusChips" :label="$t('order_status')" />
    </report-tools>

    <report-state v-if="loading && !users.length" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-off-outline" :title="emptyText"></report-state>

    <ui-table
      v-else
      variant="report"
      wrap-class="rd-card--scroll"
      table-class="rd-tbl--workers"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="sums"
      :total-label="$t('total')"
      :selected="isOpen"
      :row-class="(row) => ({ 'is-idle': row.state === 'off' })"
      @sort="sortBy"
      @open="openWorker"
    >
      <template #cell-name="{ row }">
        <span class="rd-person">
          <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ row.initials }}</span>
          <span class="rd-person__name">{{ row.name }}</span>
        </span>
      </template>

      <template #cell-status="{ row }">
        <span class="rd-status" :class="`rd-status--${row.state}`">{{ row.statusLabel }}</span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import CompanyReportTabEmployee from "@/components/company/reports/tabs/CompanyReportTabEmployee.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import ReportDesktopWorkerMonth from "@/components/report/desktop/screens/ReportDesktopWorkerMonth.vue";
import hasCompanyGroupEmployeeRows from "../../hasCompanyGroupEmployeeRows";

/**
 * Pregled korisnika firme tipa "grupa" na računaru: spisak radnika izabranih firmi sa poručeno / preuzeto /
 * razlikom i statusom (preuzimanje samo kad ga firma prati). Filter statusa su čipovi sa brojevima; klik na red
 * otvara mesečni pregled radnika u panelu. Firme, period i pretragu čita iz zajedničke trake, a radnike dobija od
 * ljuske (kao stari tab). Nasleđuje filter statusa, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopGroupEmployees",
  extends: CompanyReportTabEmployee,
  mixins: [hasCompanyGroupEmployeeRows, hasTableSort],
  components: { ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      const taken = this.canCheckTaken
        ? [
            { key: "taken", label: this.$t("taken"), cls: "rd-col-taken", align: "end", num: true, zero: true, sortable: true },
            {
              key: "difference",
              label: this.$t("difference"),
              align: "end",
              num: true,
              zero: true,
              sortable: true,
              cellClass: (row) => ({ "is-alert": row.difference > 0 }),
            },
          ]
        : [];
      return [
        { key: "id", label: "ID", cls: "rd-col-id", num: true, muted: true, sortable: true },
        { key: "name", label: this.$t("worker"), name: true, sortable: true },
        ...(this.hasExternalCode
          ? [{ key: "externalCode", label: this.$t("external_code"), num: true, muted: true, sortable: true, value: (row) => row.externalCode || "–" }]
          : []),
        ...(this.showCompanyColumn ? [{ key: "company", label: this.$t("firm"), muted: true, sortable: true, value: (row) => row.company || "–" }] : []),
        ...(this.isSingleDay
          ? [
              {
                key: "time",
                sortKey: "sortTime",
                label: this.$t("time"),
                cls: "rd-col-time",
                headTitle: this.$t("order_datetime"),
                num: true,
                muted: true,
                sortable: true,
                value: (row) => row.time || "–",
              },
            ]
          : []),
        { key: "ordered", label: this.$t("ordered"), align: "end", num: true, zero: true, sortable: true },
        ...taken,
        { key: "status", label: this.$t("status"), sortKey: "stateRank", sortable: true },
        { key: "go", label: "", go: true },
      ];
    },
    hasExternalCode() {
      return this.matchedRows.some((row) => row.externalCode);
    },
    rows() {
      return this.sortRows(this.matchedRows);
    },
    kpis() {
      const kpis = [{ key: "ordered", label: this.$t("ordered"), value: this.sums.ordered, tone: "inverse" }];
      if (this.canCheckTaken) {
        kpis.push(
          { key: "taken", label: this.$t("taken"), value: this.sums.taken },
          { key: "difference", label: this.$t("difference"), value: this.sums.difference }
        );
      }
      return kpis;
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `worker-${row.id}`);
    },
    /** Mesečni pregled radnika u panelu (isti podaci kao stari dijalog: "food-orders-by-month"). */
    openWorker(row, event) {
      const employee = this.toEmployeeContext(row);
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `worker-${row.id}`,
        root: true,
        wide: true,
        title: row.name,
        subtitle: [`ID ${row.id}`, employee.companyName].filter(Boolean).join(" · "),
        component: ReportDesktopWorkerMonth,
        props: { compact: true, worker: employee },
      });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("worker"),
        ...(this.hasExternalCode ? [this.$t("external_code")] : []),
        ...(this.showCompanyColumn ? [this.$t("firm")] : []),
        ...(this.isSingleDay ? [this.$t("time")] : []),
        this.$t("ordered"),
        ...(this.canCheckTaken ? [this.$t("taken"), this.$t("difference")] : []),
        this.$t("status"),
      ];
      const rows = this.rows.map((row) => [
        row.id,
        row.name,
        ...(this.hasExternalCode ? [row.externalCode] : []),
        ...(this.showCompanyColumn ? [row.company] : []),
        ...(this.isSingleDay ? [row.time] : []),
        row.ordered,
        ...(this.canCheckTaken ? [row.taken, row.difference] : []),
        row.statusLabel,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
